/**
 * TemplateEditorPage — /agents/templates/:templateSlug
 * Template detail on the DetailPage template. Demo page: /profile (title, tabs, side rail). The tab is kept in the URL.
 * Tabs live in ./template-detail/; the configuration tab is the edit form (entity-management §3).
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { getTemplateOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, QueryBoundary } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { PAGE_HELP } from '../../help/index.js';
import { sentenceCase } from '../../components/ui/FilterSelect.js';
import { TemplateCompareTab } from './template-detail/TemplateCompareTab.js';
import { TemplateCompatibilityTab } from './template-detail/TemplateCompatibilityTab.js';
import { TemplateConfigurationTab } from './template-detail/TemplateConfigurationTab.js';
import { TemplateHistoryTab } from './template-detail/TemplateHistoryTab.js';
import { TemplateOverviewTab } from './template-detail/TemplateOverviewTab.js';
import { TemplateRail } from './template-detail/TemplateRail.js';
import { TemplateUsageTab } from './template-detail/TemplateUsageTab.js';

const TAB_IDS = ['overview', 'configuration', 'compatibility', 'usage', 'history', 'compare'] as const;

export const TemplateEditorPage: React.FC = () => {
  const { templateSlug = '' } = useParams<{ templateSlug: string }>();
  const query = useQuery({ ...getTemplateOptions({ path: { templateSlug } }), enabled: templateSlug !== '' });
  const canManage = usePolicy('CanManageAgentTemplates');
  const [tab, setTab] = useTabParam(TAB_IDS, 'overview');

  return (
    <QueryBoundary query={query} label="the template">
      {({ template }) => {
        const tabs = [
          { id: 'overview', label: 'Overview' },
          ...(canManage ? [{ id: 'configuration', label: 'Configuration' }] : []),
          { id: 'compatibility', label: 'Compatibility' },
          { id: 'usage', label: 'Usage and quality' },
          { id: 'history', label: 'Examples and changelog' },
          { id: 'compare', label: 'Compare' },
        ];
        return (
          <DetailPage
            title={template.title}
            subtitle="Capability, compatibility, usage, versions and evaluation signals."
            breadcrumbs={[
              { label: 'Libraries', to: '/agents/libraries' },
              { label: 'Templates', to: '/agents/templates' },
              { label: template.title },
            ]}
            helpContent={PAGE_HELP['templates']}
            badges={
              <Badge variant={template.disabled ? 'danger' : 'success'} dot>
                {template.disabled ? 'Disabled' : sentenceCase(template.status ?? 'active')}
              </Badge>
            }
            actions={
              <Link
                to={`/agents/templates/${template.id}/versions`}
                className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
              >
                <History size={14} aria-hidden="true" />
                Versions
              </Link>
            }
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            rail={<TemplateRail template={template} />}
          >
            {tab === 'overview' && <TemplateOverviewTab template={template} />}
            {tab === 'configuration' && canManage && <TemplateConfigurationTab template={template} />}
            {tab === 'compatibility' && <TemplateCompatibilityTab template={template} />}
            {tab === 'usage' && <TemplateUsageTab template={template} />}
            {tab === 'history' && <TemplateHistoryTab template={template} />}
            {tab === 'compare' && <TemplateCompareTab base={template} />}
          </DetailPage>
        );
      }}
    </QueryBoundary>
  );
};
